import { useState, useEffect, useRef, useCallback } from 'react'
import { api, session, setOnExpired, uploadFiles, checkFiles, filesFromClipboard, MAX_FILE_MB } from './api'
import { Icon, StatusPill, Spinner, EmptyState, Avatar, timeAgo, fmtDateTime, formatSize, FileIcon, splitQuoted, normStatus } from '../components/ui'

const LOGO = '/logo-easytech-2.png'
const PREVIEWABLE = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/avif']

// Percorso interno dell'area clienti: #/ (elenco), #/nuovo, #/ticket/<id>, #/account
const readRoute = () => {
  const h = window.location.hash.replace(/^#\/?/, '')
  const [page, id] = h.split('/')
  return { page: page || 'list', id: id || null }
}
const go = (path) => { window.location.hash = '#/' + path }

function Alert({ type = 'error', children, onClose }) {
  const icon = type === 'success' ? 'checkCircle' : 'alert'
  return (
    <div className={`alert alert-${type}`} style={{ marginBottom: 14 }}>
      <Icon name={icon} size={16} /> <span className="grow">{children}</span>
      {onClose && <button className="alert-close" onClick={onClose} title="Chiudi"><Icon name="x" size={14} /></button>}
    </div>
  )
}

// ───────────────────────── Accesso ─────────────────────────
function AuthScreen({ onLoggedIn, resetToken, verifyToken, clearUrl }) {
  const [mode, setMode] = useState(resetToken ? 'reset' : 'login')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(!!verifyToken)
  const verifying = useRef(false)

  // Link di conferma email aperto dalla casella: si attiva l'account ed si entra
  useEffect(() => {
    if (!verifyToken || verifying.current) return
    verifying.current = true
    api('POST', '/verify', { token: verifyToken })
      .then(out => { clearUrl(); onLoggedIn(out.token) })
      .catch(e => { clearUrl(); setError(e.message); setLoading(false) })
  }, [verifyToken, onLoggedIn, clearUrl])

  const switchMode = (m) => { setMode(m); setError(''); setMessage(''); setPassword(''); setPassword2('') }

  const submit = async () => {
    setError(''); setMessage('')
    if ((mode === 'register' || mode === 'reset') && password !== password2) { setError('Le due password non coincidono.'); return }
    setLoading(true)
    try {
      if (mode === 'login') {
        const out = await api('POST', '/login', { email, password })
        onLoggedIn(out.token)
        return
      }
      if (mode === 'register') {
        await api('POST', '/register', { name, email, password })
        setMode('login'); setPassword(''); setPassword2('')
        setMessage(`Ti abbiamo inviato un'email a ${email.trim()}: apri il link per attivare l'accesso. Controlla anche lo spam.`)
      } else if (mode === 'forgot') {
        await api('POST', '/forgot', { email })
        setMessage(`Se ${email.trim()} ha un accesso, riceverai a breve un'email con il link per scegliere una nuova password.`)
      } else if (mode === 'reset') {
        const out = await api('POST', '/reset', { token: resetToken, password })
        clearUrl()
        onLoggedIn(out.token)
        return
      }
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }
  const onKey = (e) => { if (e.key === 'Enter') submit() }

  const titles = {
    login: ['Area clienti', 'Accedi per vedere le tue richieste di assistenza'],
    register: ['Crea il tuo accesso', "Usa l'email con cui ci scrivi: vedrai tutte le tue richieste"],
    forgot: ['Password dimenticata', "Ti mandiamo un link per sceglierne una nuova"],
    reset: ['Nuova password', 'Scegli la nuova password per entrare'],
  }

  return (
    <div className="auth">
      <div className="auth-brand">
        <div><img src={LOGO} alt="Easytech" style={{ height: 72, margin: '-18px -14px', filter: 'brightness(0) invert(1)' }} /></div>
        <div>
          <h1 className="auth-headline">Le tue richieste di assistenza, sempre a portata di mano.</h1>
          <p className="auth-lead">Segui lo stato di ogni richiesta, leggi le nostre risposte e scrivici quando vuoi, anche con allegati.</p>
          <div className="auth-feat"><span className="auth-feat-ico"><Icon name="inbox" size={15} /></span><span>Tutte le richieste in un unico posto, anche quelle inviate per email</span></div>
          <div className="auth-feat"><span className="auth-feat-ico"><Icon name="message" size={15} /></span><span>Rispondi e apri nuove richieste direttamente da qui</span></div>
          <div className="auth-feat"><span className="auth-feat-ico"><Icon name="paperclip" size={15} /></span><span>Foto, documenti e fatture sempre allegati alla conversazione</span></div>
        </div>
        <div style={{ fontSize: 12, color: '#6b768c' }}>© {new Date().getFullYear()} Easytech</div>
      </div>

      <div className="auth-form-wrap">
        <div className="auth-form">
          <div className="p-mobile-logo"><img src={LOGO} alt="Easytech" /></div>
          <h2 className="page-title" style={{ fontSize: 24 }}>{titles[mode][0]}</h2>
          <p className="page-sub" style={{ marginBottom: 28 }}>{titles[mode][1]}</p>

          {verifyToken && loading ? (
            <div className="row" style={{ gap: 10, color: 'var(--text-3)' }}><Spinner size={16} /> Attivazione dell'accesso…</div>
          ) : (
            <>
              {mode === 'register' && (
                <label className="field">
                  <span className="label">Nome e cognome</span>
                  <input className="input" placeholder="Mario Rossi" value={name} onChange={e => setName(e.target.value)} onKeyDown={onKey} autoComplete="name" />
                </label>
              )}
              {mode !== 'reset' && (
                <label className="field">
                  <span className="label">Email</span>
                  <input className="input" type="email" placeholder="nome@azienda.it" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={onKey} autoComplete="email" autoFocus />
                </label>
              )}
              {mode !== 'forgot' && (
                <label className="field">
                  <span className="label">{mode === 'login' ? 'Password' : 'Nuova password'}</span>
                  <input className="input" type="password" placeholder={mode === 'login' ? '••••••••' : 'Almeno 8 caratteri'} value={password}
                    onChange={e => setPassword(e.target.value)} onKeyDown={onKey} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} autoFocus={mode === 'reset'} />
                </label>
              )}
              {(mode === 'register' || mode === 'reset') && (
                <label className="field">
                  <span className="label">Ripeti la password</span>
                  <input className="input" type="password" value={password2} onChange={e => setPassword2(e.target.value)} onKeyDown={onKey} autoComplete="new-password" />
                </label>
              )}
              {mode === 'login' && (
                <div style={{ textAlign: 'right', marginTop: -6, marginBottom: 14 }}>
                  <button className="btn-link" style={{ border: 0, background: 'none', color: 'var(--accent-text)', fontSize: 13 }} onClick={() => switchMode('forgot')}>Password dimenticata?</button>
                </div>
              )}

              {error && <Alert>{error}</Alert>}
              {message && <Alert type="success">{message}</Alert>}

              <button className="btn btn-primary btn-block" style={{ height: 42, fontSize: 14 }} onClick={submit} disabled={loading}>
                {loading && <Spinner size={14} />}
                {loading ? 'Attendere…' : { login: 'Accedi', register: 'Crea accesso', forgot: 'Invia il link', reset: 'Salva e accedi' }[mode]}
              </button>

              <div style={{ textAlign: 'center', marginTop: 22, fontSize: 13, color: 'var(--text-3)' }}>
                {mode === 'login'
                  ? <>Prima volta qui? <button className="btn-link" style={{ border: 0, background: 'none', color: 'var(--accent-text)', fontWeight: 600 }} onClick={() => switchMode('register')}>Crea il tuo accesso</button></>
                  : <button className="btn-link" style={{ border: 0, background: 'none', color: 'var(--accent-text)', fontWeight: 600 }} onClick={() => switchMode('login')}>Torna all'accesso</button>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ───────────────────────── Elenco ─────────────────────────
function TicketList({ me }) {
  const [tickets, setTickets] = useState(null)
  const [error, setError] = useState('')
  const [tab, setTab] = useState('open')
  const [search, setSearch] = useState('')

  useEffect(() => {
    api('GET', '/tickets').then(setTickets).catch(e => { setError(e.message); setTickets([]) })
  }, [])

  const isOpen = (t) => normStatus(t.status) !== 'risolto'
  const all = tickets || []
  const q = search.trim().toLowerCase()
  const shown = all
    .filter(t => tab === 'all' || (tab === 'open' ? isOpen(t) : !isOpen(t)))
    .filter(t => !q || (t.title || '').toLowerCase().includes(q) || String(t.number || '').includes(q.replace('#', '')))
  const multiEmail = (me?.emails || []).length > 1

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Le mie richieste</h1>
          <p className="page-sub">{me?.customer ? `${me.customer} · ` : ''}Tutte le richieste di assistenza{multiEmail ? ' dei tuoi indirizzi email' : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={() => go('nuovo')}><Icon name="plus" size={15} /> Nuova richiesta</button>
      </div>

      {error && <Alert>{error}</Alert>}

      <div className="card">
        <div className="toolbar" style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="seg">
            {[['open', 'In corso', all.filter(isOpen).length], ['closed', 'Risolte', all.filter(t => !isOpen(t)).length], ['all', 'Tutte', all.length]].map(([k, label, n]) => (
              <button key={k} className={`seg-btn${tab === k ? ' active' : ''}`} onClick={() => setTab(k)}>{label} <span className="faint">{n}</span></button>
            ))}
          </div>
          <div className="search" style={{ flex: '1 1 220px', maxWidth: 320, marginLeft: 'auto' }}>
            <Icon name="search" size={16} className="search-ico" />
            <input className="input" placeholder="Cerca per oggetto o numero" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        {tickets === null ? (
          <div className="screen-center" style={{ padding: 48 }}><Spinner size={20} /></div>
        ) : shown.length === 0 ? (
          <EmptyState icon="inbox"
            title={all.length === 0 ? 'Nessuna richiesta' : 'Nessuna richiesta in questo elenco'}
            text={all.length === 0 ? 'Quando ci scrivi, qui trovi le tue richieste e le nostre risposte.' : 'Prova a cambiare il filtro o la ricerca.'}
            action={all.length === 0 && <button className="btn btn-primary" onClick={() => go('nuovo')}><Icon name="plus" size={15} /> Nuova richiesta</button>} />
        ) : (
          <div className="p-list">
            {shown.map(t => (
              <button key={t.id} className="p-item" onClick={() => go('ticket/' + t.id)}>
                <span className="p-num">#{t.number}</span>
                <div className="grow minw0 p-main">
                  <div className="p-title truncate">{t.title}</div>
                  <div className="p-sub truncate">Aperta il {fmtDateTime(t.created_at)}{multiEmail ? ` · ${t.email}` : ''}</div>
                </div>
                {t.answered && isOpen(t) && <span className="p-new"><Icon name="message" size={11} /> Risposta ricevuta</span>}
                <StatusPill status={t.status} size="sm" />
                <span className="p-time" title={fmtDateTime(t.last_activity)}>{timeAgo(t.last_activity)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

// ───────────────────────── Allegati ─────────────────────────
function PortalAttachment({ att }) {
  const [url, setUrl] = useState(null)
  const [err, setErr] = useState('')
  const isImg = PREVIEWABLE.includes((att.type || '').toLowerCase())

  useEffect(() => {
    if (!isImg) return
    let alive = true
    api('GET', `/attachments/${att.id}`).then(o => alive && setUrl(o.url)).catch(() => {})
    return () => { alive = false }
  }, [att.id, isImg])

  const download = async () => {
    setErr('')
    try {
      const o = await api('GET', `/attachments/${att.id}?download=1`)
      window.location.href = o.url
    } catch (e) { setErr(e.message) }
  }

  if (isImg && url) {
    return (
      <a className="att-thumb" href={url} target="_blank" rel="noopener noreferrer" title={att.name}>
        <img src={url} alt={att.name} onError={() => setUrl(null)} />
      </a>
    )
  }
  return (
    <button className="att-chip" onClick={download} title={err || `Scarica ${att.name}`}>
      <FileIcon type={att.type} size={15} />
      <span className="truncate">{att.name}</span>
      <span className="att-size">{err ? 'non disponibile' : formatSize(att.size)}</span>
    </button>
  )
}

function MessageText({ text }) {
  const [open, setOpen] = useState(false)
  const { main, quoted, label } = splitQuoted(text)
  return (
    <>
      <div className="msg-body">{main}</div>
      {quoted && (
        <>
          <button className={`quote-toggle${open ? ' open' : ''}`} onClick={() => setOpen(o => !o)}>
            <span className="quote-dots">•••</span>
            <span className="truncate">{open ? 'Nascondi' : label}</span>
          </button>
          {open && <div className="msg-quoted">{quoted}</div>}
        </>
      )}
    </>
  )
}

// File scelti, trascinati o incollati, in attesa di invio
function useFiles(setError) {
  const [files, setFiles] = useState([])
  const add = (list) => {
    const next = [...files, ...list]
    const problem = checkFiles(next)
    if (problem) { setError(problem); return }
    setError('')
    setFiles(next)
  }
  return {
    files, add, clear: () => setFiles([]),
    remove: (i) => setFiles(prev => prev.filter((_, n) => n !== i)),
    onInput: (e) => { const l = Array.from(e.target.files || []); e.target.value = ''; if (l.length) add(l) },
    onPaste: (e) => { const l = filesFromClipboard(e); if (l.length) { e.preventDefault(); add(l) } },
    onDrop: (e) => { e.preventDefault(); const l = Array.from(e.dataTransfer?.files || []); if (l.length) add(l) },
  }
}

function FilePills({ files, onRemove }) {
  if (!files.length) return null
  return (
    <div className="composer-files">
      {files.map((f, i) => (
        <span key={i} className="file-pill">
          <FileIcon type={f.type} size={14} />
          <span className="truncate">{f.name}</span>
          <span className="faint">{formatSize(f.size)}</span>
          <button type="button" onClick={() => onRemove(i)} title="Rimuovi"><Icon name="x" size={13} /></button>
        </span>
      ))}
    </div>
  )
}

// ───────────────────────── Ticket ─────────────────────────
function TicketView({ id, me }) {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [text, setText] = useState('')
  const [sendError, setSendError] = useState('')
  const [sending, setSending] = useState(false)
  const [drag, setDrag] = useState(false)
  const lastRef = useRef(null)
  const scrollNext = useRef('auto')
  const fs = useFiles(setSendError)

  const load = useCallback(() => api('GET', `/tickets/${id}`).then(setData).catch(e => setError(e.message)), [id])
  useEffect(() => { load() }, [load])

  // Si apre sull'ultimo messaggio; dopo un invio ci si scorre dolcemente
  useEffect(() => {
    if (!data || !scrollNext.current) return
    const behavior = scrollNext.current
    scrollNext.current = null
    requestAnimationFrame(() => {
      const el = lastRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      if (r.top < 0 || r.bottom > window.innerHeight) el.scrollIntoView({ block: 'start', behavior })
    })
  }, [data])

  const send = async () => {
    if ((!text.trim() && !fs.files.length) || sending) return
    setSending(true); setSendError('')
    try {
      const attachments = await uploadFiles(fs.files)
      await api('POST', `/tickets/${id}/reply`, { message: text, attachments })
      setText(''); fs.clear()
      scrollNext.current = 'smooth'
      await load()
    } catch (e) {
      setSendError(`Messaggio non inviato: ${e.message}`)
    }
    setSending(false)
  }

  if (error) return (
    <>
      <div className="crumbs" style={{ marginBottom: 12 }}><button onClick={() => go('')}><Icon name="arrowLeft" size={15} /> Le mie richieste</button></div>
      <Alert>{error}</Alert>
    </>
  )
  if (!data) return <div className="screen-center" style={{ padding: 60 }}><Spinner size={22} /></div>

  const { ticket, messages, attachments } = data
  const byComment = {}
  attachments.forEach(a => { if (a.comment_id) (byComment[a.comment_id] = byComment[a.comment_id] || []).push(a) })
  const opening = attachments.filter(a => !a.comment_id)
  const atts = (list) => list.length > 0 && <div className="att-row">{list.map(a => <PortalAttachment key={a.id} att={a} />)}</div>
  const resolved = normStatus(ticket.status) === 'risolto'
  // La richiesta iniziale e' del cliente se e' arrivata per email o dall'area clienti
  const openerIsCustomer = ticket.via_email

  return (
    <>
      <div className="crumbs" style={{ marginBottom: 12 }}>
        <button onClick={() => go('')}><Icon name="arrowLeft" size={15} /> Le mie richieste</button>
        <span className="sep">/</span><span>#{ticket.number}</span>
      </div>

      <div className="card detail-head">
        <div className="minw0" style={{ flex: '1 1 420px' }}>
          <div className="dh-meta"><span className="dh-num">#{ticket.number}</span><StatusPill status={ticket.status} /></div>
          <h1 className="dh-title">{ticket.title}</h1>
          <div className="dh-sub"><Icon name="calendar" size={14} /> Aperta il {fmtDateTime(ticket.created_at)}{(me?.emails || []).length > 1 && <><span className="faint">·</span> {ticket.email}</>}</div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header">
          <div className="row"><span className="card-title">Conversazione</span><span className="badge">{messages.length + 1}</span></div>
        </div>
        <div className="timeline">
          <article className="msg msg-desc" ref={messages.length === 0 ? lastRef : undefined}>
            <Avatar name={openerIsCustomer ? ticket.email : 'Assistenza'} size={34} />
            <div className="msg-main">
              <header className="msg-head">
                <span className="msg-name">{openerIsCustomer ? 'Tu' : 'Assistenza'}</span>
                <span className="msg-via">Richiesta iniziale</span>
                <time className="msg-time">{fmtDateTime(ticket.created_at)}</time>
              </header>
              {ticket.description && <MessageText text={ticket.description} />}
              {atts(opening)}
            </div>
          </article>

          {messages.map((m, i) => {
            const mine = m.from === 'customer'
            return (
              <article key={m.id} ref={i === messages.length - 1 ? lastRef : undefined} className={`msg ${mine ? 'msg-customer' : 'msg-agent'}`}>
                <Avatar name={mine ? m.sender : m.name} size={34} />
                <div className="msg-main">
                  <header className="msg-head">
                    <span className="msg-name">{mine ? (m.sender && m.sender.toLowerCase() !== (me?.email || '') ? m.sender : 'Tu') : m.name}</span>
                    {!mine && <span className="tag tag-agent">Assistenza</span>}
                    {mine && <span className="msg-via"><Icon name={m.via === 'portal' ? 'user' : 'mail'} size={12} /> {m.via === 'portal' ? "dall'area clienti" : 'via email'}</span>}
                    <time className="msg-time">{fmtDateTime(m.created_at)}</time>
                  </header>
                  <MessageText text={m.text} />
                  {atts(byComment[m.id] || [])}
                </div>
              </article>
            )
          })}
        </div>

        <div className="composer">
          {resolved && <Alert type="info">Questa richiesta è risolta. Se ci scrivi, la riapriamo.</Alert>}
          {sendError && <Alert onClose={() => setSendError('')}>{sendError}</Alert>}
          <div className={`composer-box${drag ? ' drag' : ''}`}
            onDragOver={e => { e.preventDefault(); setDrag(true) }}
            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDrag(false) }}
            onDrop={e => { setDrag(false); fs.onDrop(e) }}>
            <textarea className="composer-input" value={text} onChange={e => setText(e.target.value)} onPaste={fs.onPaste}
              onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); send() } }}
              placeholder="Scrivi un messaggio…" />
            <FilePills files={fs.files} onRemove={fs.remove} />
          </div>
          <div className="composer-foot">
            <label className="btn btn-ghost btn-sm" title={`Allega file (max ${MAX_FILE_MB} MB ciascuno)`}>
              <Icon name="paperclip" size={15} /> Allega
              <input type="file" multiple hidden onChange={fs.onInput} />
            </label>
            <span className="kbd-hint">Trascina o incolla i file · <span className="kbd">Ctrl</span> + <span className="kbd">Invio</span> per inviare</span>
            <button className="btn btn-primary" onClick={send} disabled={sending || (!text.trim() && !fs.files.length)}>
              {sending ? <Spinner size={14} /> : <Icon name="send" size={15} />} {sending ? (fs.files.length ? 'Caricamento…' : 'Invio…') : 'Invia'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

// ───────────────────────── Nuova richiesta ─────────────────────────
function NewRequest({ me }) {
  const deps = me?.departments || []
  const [department, setDepartment] = useState(deps[0]?.key || '')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState('')
  const [sending, setSending] = useState(false)
  const [drag, setDrag] = useState(false)
  const fs = useFiles(setError)

  useEffect(() => { if (!department && deps[0]) setDepartment(deps[0].key) }, [deps, department])

  const submit = async () => {
    if (!title.trim()) { setError("Scrivi l'oggetto della richiesta."); return }
    if (!description.trim()) { setError('Descrivi la tua richiesta.'); return }
    setSending(true); setError('')
    try {
      const attachments = await uploadFiles(fs.files)
      const out = await api('POST', '/tickets', { title, description, department, attachments })
      go('ticket/' + out.id)
    } catch (e) {
      setError(`Richiesta non inviata: ${e.message}`)
      setSending(false)
    }
  }

  return (
    <div className="portal-narrow" style={{ margin: '0 auto' }}>
      <div className="crumbs" style={{ marginBottom: 12 }}>
        <button onClick={() => go('')}><Icon name="arrowLeft" size={15} /> Le mie richieste</button>
        <span className="sep">/</span><span>Nuova</span>
      </div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Nuova richiesta</h1>
          <p className="page-sub">Ti risponderemo qui e per email a {me?.email}</p>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          {deps.length > 1 && (
            <div className="field">
              <span className="label">A chi vuoi scrivere</span>
              <div className="seg">
                {deps.map(d => (
                  <button key={d.key} className={`seg-btn${department === d.key ? ' active' : ''}`} onClick={() => setDepartment(d.key)}>{d.name}</button>
                ))}
              </div>
            </div>
          )}
          <label className="field">
            <span className="label">Oggetto<span className="req">*</span></span>
            <input className="input" placeholder="Es. Ordine 957 non ancora arrivato" value={title} onChange={e => setTitle(e.target.value)} maxLength={200} />
          </label>
          <label className="field">
            <span className="label">Descrizione<span className="req">*</span></span>
            <textarea className="textarea" rows={8} placeholder="Raccontaci cosa è successo: numero d'ordine, prodotto, cosa ti serve…"
              value={description} onChange={e => setDescription(e.target.value)} onPaste={fs.onPaste} />
          </label>
          <div className="field" style={{ marginBottom: 0 }}>
            <span className="label">Allegati</span>
            <label className={`dropzone${drag ? ' drag' : ''}`}
              onDragOver={e => { e.preventDefault(); setDrag(true) }} onDragLeave={() => setDrag(false)}
              onDrop={e => { setDrag(false); fs.onDrop(e) }}>
              <span className="dz-ico"><Icon name="upload" size={18} /></span>
              <span><strong>Clicca per caricare</strong> oppure trascina qui i file</span>
              <span className="hint">Foto, PDF, documenti fino a {MAX_FILE_MB} MB — puoi anche incollare uno screenshot nella descrizione</span>
              <input type="file" multiple hidden onChange={fs.onInput} />
            </label>
            <FilePills files={fs.files} onRemove={fs.remove} />
          </div>
          {error && <div style={{ marginTop: 16 }}><Alert>{error}</Alert></div>}
        </div>
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => go('')} disabled={sending}>Annulla</button>
          <button className="btn btn-primary" onClick={submit} disabled={sending}>
            {sending ? <Spinner size={14} /> : <Icon name="send" size={15} />} {sending ? (fs.files.length ? 'Caricamento allegati…' : 'Invio…') : 'Invia richiesta'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ───────────────────────── Indirizzi collegati ─────────────────────────
// Il cliente aggiunge un indirizzo: il link di conferma parte verso QUELL'indirizzo
function LinkedEmails({ me, onChanged }) {
  const [email, setEmail] = useState('')
  const [msg, setMsg] = useState(null)
  const [busy, setBusy] = useState(false)

  const add = async () => {
    const e = email.trim()
    if (!e) return
    setBusy(true); setMsg(null)
    try {
      await api('POST', '/emails', { email: e })
      setEmail('')
      setMsg({ type: 'success', text: `Ti abbiamo inviato un link a ${e}: aprilo da quella casella per completare il collegamento (controlla anche lo spam).` })
    } catch (err) { setMsg({ type: 'error', text: err.message }) }
    setBusy(false)
  }

  const remove = async (e) => {
    if (!window.confirm(`Scollegare ${e}?\nNon vedrai più qui le richieste inviate da quell'indirizzo.`)) return
    setMsg(null)
    try { await api('POST', '/emails/remove', { email: e }); onChanged() }
    catch (err) { setMsg({ type: 'error', text: err.message }) }
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <span className="card-title">Indirizzi email collegati</span>
        <span className="card-sub">Vedi le richieste di tutti questi indirizzi</span>
      </div>
      <div className="card-body">
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginBottom: 14 }}>
          {(me?.emails || []).map(e => (
            <div key={e} className="row" style={{ gap: 10, padding: '9px 12px', borderBottom: '1px solid var(--border)' }}>
              <Icon name="mail" size={14} style={{ color: 'var(--text-4)' }} />
              <span className="grow truncate">{e}</span>
              {e === me.email
                ? <span className="faint" style={{ fontSize: 12 }}>accesso</span>
                : <button className="btn btn-ghost btn-sm btn-icon" onClick={() => remove(e)} title="Scollega"><Icon name="x" size={14} /></button>}
            </div>
          ))}
        </div>
        <span className="label">Collega un altro indirizzo</span>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
          <input className="input" style={{ flex: '1 1 220px' }} type="email" placeholder="altro.indirizzo@azienda.it" value={email}
            onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} />
          <button className="btn btn-primary" onClick={add} disabled={busy || !email.trim()}>
            {busy ? <Spinner size={14} /> : <Icon name="send" size={15} />} Invia conferma
          </button>
        </div>
        <span className="hint" style={{ display: 'block', marginTop: 6, marginBottom: msg ? 12 : 0 }}>
          Per sicurezza inviamo un link a quell'indirizzo: il collegamento avviene solo quando lo apri da lì.
        </span>
        {msg && <Alert type={msg.type} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      </div>
    </div>
  )
}

// Pagina aperta dal link di collegamento (anche senza essere entrati: puo' aprirla un collega)
function LinkConfirm({ token, onDone }) {
  const [state, setState] = useState({ loading: true })
  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    api('POST', '/emails/confirm', { token })
      .then(out => setState({ ok: true, email: out.email, already: out.already }))
      .catch(e => setState({ error: e.message }))
  }, [token])

  return (
    <div className="screen-center" style={{ minHeight: '100vh', padding: 20 }}>
      <div className="card" style={{ maxWidth: 440, width: '100%', padding: 28, textAlign: 'center' }}>
        <img src={LOGO} alt="Easytech" style={{ height: 72, margin: '-18px auto -6px', display: 'block' }} />
        {state.loading && <div className="row" style={{ gap: 10, justifyContent: 'center', color: 'var(--text-3)', marginTop: 12 }}><Spinner size={16} /> Collegamento in corso…</div>}
        {state.ok && (
          <>
            <h2 className="page-title" style={{ fontSize: 20, marginTop: 8 }}>Indirizzo collegato</h2>
            <p className="page-sub" style={{ margin: '8px 0 20px' }}>
              {state.already ? `${state.email} era già collegato.` : `Da ora nell'area clienti si vedono anche le richieste di ${state.email}.`}
            </p>
          </>
        )}
        {state.error && <div style={{ marginTop: 12, textAlign: 'left' }}><Alert>{state.error}</Alert></div>}
        {!state.loading && <button className="btn btn-primary" onClick={onDone}>Vai all'area clienti</button>}
      </div>
    </div>
  )
}

// ───────────────────────── Account ─────────────────────────
function Account({ me, onToken, onLogout, onMeChanged }) {
  const [current, setCurrent] = useState('')
  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')
  const [msg, setMsg] = useState(null)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setMsg(null)
    if (password !== password2) { setMsg({ type: 'error', text: 'Le due password non coincidono.' }); return }
    setSaving(true)
    try {
      const out = await api('POST', '/password', { current, password })
      onToken(out.token)
      setCurrent(''); setPassword(''); setPassword2('')
      setMsg({ type: 'success', text: 'Password cambiata.' })
    } catch (e) { setMsg({ type: 'error', text: e.message }) }
    setSaving(false)
  }

  return (
    <div className="portal-narrow" style={{ margin: '0 auto' }}>
      <div className="page-header">
        <div><h1 className="page-title">Il tuo account</h1><p className="page-sub">Dati di accesso all'area clienti</p></div>
        <button className="btn btn-secondary" onClick={onLogout}><Icon name="logout" size={15} /> Esci</button>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body">
          <dl className="props">
            <dt>Nome</dt><dd>{me?.name || '—'}</dd>
            <dt>Email di accesso</dt><dd>{me?.email}</dd>
            {me?.customer && <><dt>Cliente</dt><dd>{me.customer}</dd></>}
          </dl>
        </div>
      </div>

      <LinkedEmails me={me} onChanged={onMeChanged} />

      <div className="card">
        <div className="card-header"><span className="card-title">Cambia password</span></div>
        <div className="card-body">
          <label className="field"><span className="label">Password attuale</span>
            <input className="input" type="password" value={current} onChange={e => setCurrent(e.target.value)} autoComplete="current-password" /></label>
          <label className="field"><span className="label">Nuova password</span>
            <input className="input" type="password" placeholder="Almeno 8 caratteri" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" /></label>
          <label className="field"><span className="label">Ripeti la nuova password</span>
            <input className="input" type="password" value={password2} onChange={e => setPassword2(e.target.value)} autoComplete="new-password" /></label>
          {msg && <Alert type={msg.type}>{msg.text}</Alert>}
          <button className="btn btn-primary" onClick={save} disabled={saving || !current || !password}>
            {saving ? <Spinner size={14} /> : <Icon name="save" size={15} />} Salva password
          </button>
        </div>
      </div>
    </div>
  )
}

// ───────────────────────── Contenitore ─────────────────────────
export default function Portal() {
  const params = new URLSearchParams(window.location.search)
  const [verifyToken] = useState(params.get('conferma'))
  const [resetToken] = useState(params.get('reimposta'))
  const [linkToken, setLinkToken] = useState(params.get('collega'))
  const [token, setToken] = useState(() => (params.get('conferma') || params.get('reimposta')) ? null : session.get())
  const [me, setMe] = useState(null)
  const [route, setRoute] = useState(readRoute())
  const [loadError, setLoadError] = useState('')

  useEffect(() => { document.title = 'Area clienti — Easytech' }, [])
  useEffect(() => {
    const onHash = () => { setRoute(readRoute()); window.scrollTo(0, 0) }
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])
  useEffect(() => { setOnExpired(() => { setToken(null); setMe(null) }) }, [])

  const loadMe = useCallback(() => {
    setLoadError('')
    return api('GET', '/me').then(setMe).catch(e => { if (e.status !== 401) setLoadError(e.message) })
  }, [])
  useEffect(() => { if (token) loadMe() }, [token, loadMe])

  const clearUrl = useCallback(() => window.history.replaceState(null, '', window.location.pathname + window.location.hash), [])
  const loggedIn = useCallback((t) => { session.set(t); setToken(t) }, [])
  const logout = () => { session.set(null); setToken(null); setMe(null); go('') }

  if (linkToken) return <LinkConfirm token={linkToken} onDone={() => { clearUrl(); setLinkToken(null); if (token) { loadMe(); go('') } }} />
  if (!token) return <AuthScreen onLoggedIn={loggedIn} verifyToken={verifyToken} resetToken={resetToken} clearUrl={clearUrl} />

  const page = route.page
  return (
    <div className="portal">
      <header className="portal-top">
        <div className="portal-top-in">
          <button className="portal-brand" onClick={() => go('')} title="Le mie richieste">
            <img src={LOGO} alt="Easytech" />
            <span className="portal-brand-tag">Area clienti</span>
          </button>
          <nav className="portal-nav">
            <button className={`portal-link${page === 'list' || page === 'ticket' ? ' active' : ''}`} onClick={() => go('')}>
              <Icon name="inbox" size={15} style={{ verticalAlign: '-3px' }} /> <span className="portal-link-text">Le mie richieste</span>
            </button>
            <button className={`portal-link${page === 'nuovo' ? ' active' : ''}`} onClick={() => go('nuovo')}>
              <Icon name="plus" size={15} style={{ verticalAlign: '-3px' }} /> <span className="portal-link-text">Nuova richiesta</span>
            </button>
          </nav>
          <div className="portal-user">
            <button className={`portal-link${page === 'account' ? ' active' : ''}`} onClick={() => go('account')} title="Il tuo account" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar name={me?.name || me?.email || ''} size={26} />
              <span className="portal-user-name truncate" style={{ maxWidth: 180 }}>{me?.name || me?.email || ''}</span>
            </button>
            <button className="btn btn-ghost btn-sm btn-icon" onClick={logout} title="Esci"><Icon name="logout" size={16} /></button>
          </div>
        </div>
      </header>

      <main className="portal-main">
        {loadError && <Alert>{loadError}</Alert>}
        {!me ? (
          <div className="screen-center" style={{ padding: 60 }}><Spinner size={22} /></div>
        ) : page === 'ticket' && route.id ? (
          <TicketView key={route.id} id={route.id} me={me} />
        ) : page === 'nuovo' ? (
          <NewRequest me={me} />
        ) : page === 'account' ? (
          <Account me={me} onToken={loggedIn} onLogout={logout} onMeChanged={loadMe} />
        ) : (
          <TicketList me={me} />
        )}
      </main>
    </div>
  )
}
